import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearFleetControlRequest,
  useFleetControlRequests,
} from "../../utils/fleet_control_request_store";
import { Map, Marker, type MapRef } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  clusterByPixelDistance,
  getBounds,
  type FleetCluster,
} from "../../utils/fleet_clustering";
import type { MergedVehicleInfo, ConnectionState } from "../../interface/auth_types";
import { accountSource, type VehicleMeta } from "../../config/integrations";
import { useWheelMenuNav, type WheelMenuEvent } from "../../hooks/useWheelMenuNav";
import { WheelHelpOverlay } from "./wheel_help_overlay";
import PedalProfileModal from "../../app/common/pedal_profile_modal";
import "./fleet_select.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FleetSelectProps {
  vehicleList: MergedVehicleInfo[];
  signalingStatus: ConnectionState;
  mqttStatus: ConnectionState;
  signalingError: string | null;
  mqttError: string | null;
  selectionError: string | null;
  shouldShowEmptyState: boolean;
  selectedVehicleId: string;
  loginId: string;
  onLogout: () => void;
  onReload: () => void;
  onSelectVehicle: (vehicleId: string) => void;
  /** Connect using current selectedVehicleId. */
  onConnect: () => void;
  /** Select + immediately connect using a specific vehicleId (avoids stale closure). */
  onSelectAndConnect: (vehicleId: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatConnectionState(state: ConnectionState): string {
  switch (state) {
    case "connected": return "connected";
    case "loading": return "loading";
    case "error": return "failed";
    default: return "idle";
  }
}

function buildVehicleLabel(vehicleId: string, vehicle: MergedVehicleInfo): string {
  const mqttText = vehicle.mqttState || "n/a";
  const signalingText = vehicle.signalingStatus || "n/a";
  return `${vehicleId} (MQTT: ${mqttText} / Signaling: ${signalingText})`;
}

// 차량 MQTT 가 연결된 상태인지 (연결 안되면 카드를 희미하게 처리).
function isMqttConnected(mqttState: string | null): boolean {
  const s = (mqttState || "").toUpperCase();
  return s === "IDLE" || s === "BUSY" || s === "AVAILABLE";
}

// signaling 가용 여부 — connect 동작 게이트.
function isSignalingAvailable(sigStatus: string | null): boolean {
  return (sigStatus || "").toLowerCase() === "available";
}

// 차량 "선택/연결" 가능 여부 — MQTT 가 있어야(수신 중) 하고 signaling 도 가용해야 한다.
// 사용자 요구: MQTT 없으면 선택 자체가 불가.
function canSelectVehicle(vehicle: {
  signalingStatus: string | null;
  mqttState: string | null;
}): boolean {
  return (
    isMqttConnected(vehicle.mqttState) &&
    isSignalingAvailable(vehicle.signalingStatus)
  );
}

// 상태 점 색 매핑: 초록(가능) / 주황(연결중·바쁨) / 빨강(에러) / 회색(불가).
function getStatusDotClass(sigStatus: string | null): "ok" | "warn" | "err" | "muted" {
  const s = (sigStatus || "").toLowerCase();
  if (s === "available") return "ok";
  if (s === "busy" || s === "connecting" || s === "loading") return "warn";
  if (s === "error" || s === "failed") return "err";
  return "muted";
}

function getInitials(nickname: string): string {
  if (!nickname) return "?";
  const parts = nickname.split(/[\s\-—–]+/).filter(Boolean);
  if (parts.length >= 2) {
    // 한글 등 각 파트가 단일 글자인 경우 포함하여 처음 2 파트의 첫 글자를 조합.
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  // 단일 파트: 최대 2 글자(한글 복합어는 앞 2자로 충분히 구분 가능).
  const trimmed = parts[0] ?? nickname;
  if (!trimmed) return "?";
  // 유니코드 코드포인트 기준으로 앞 1-2자 추출 — 이모지·서로게이트 쌍 안전.
  const chars = [...trimmed];
  return chars.slice(0, 2).join("").toUpperCase();
}

// ---------------------------------------------------------------------------
// Map constants
// ---------------------------------------------------------------------------

const FLEET_MAP_CENTER = { longitude: 127.0427429, latitude: 37.4999608 };
// 지도 초기 zoom — 위치 보고 차량이 있으면 곧바로 그 분포로 fitBounds 하므로 임시값이다.
const FLEET_MAP_INITIAL_ZOOM = 13.5;
// 화면상 이 픽셀 반경 안의 차량은 하나의 묶음(숫자 배지)으로 접는다.
const CLUSTER_RADIUS_PX = 64;
// 묶음/전체 보기로 이동할 때의 여백과 최대 확대율. 한 지점에 겹친 차량들에서
// 무한히 확대되는 것을 막는다.
const FIT_BOUNDS_PADDING_PX = 72;
const FIT_BOUNDS_MAX_ZOOM = 17;
// 휠로 차량을 옮겨 고를 때 최소한 이 정도까지는 당겨서 보여준다. 전체 분포로 넓게 본
// 상태에서 중심만 옮기면 어느 차량인지 알아볼 수 없기 때문. 이미 더 확대돼 있으면 유지.
const FOCUS_MIN_ZOOM = 15;
const MAPBOX_TOKEN = "pk.eyJ1IjoibHd0MTg0OSIsImEiOiJja2E4cmNkeDQwM3FnMnRtcG8yZjZoNnBpIn0.bYtW4sYdUNocTLkByqhuwA";
const MAPBOX_STYLE = "mapbox://styles/mapbox/dark-v10";

// ---------------------------------------------------------------------------
// FleetSelect component
// ---------------------------------------------------------------------------

const FleetSelectComponent = (props: FleetSelectProps) => {
  const {
    vehicleList,
    signalingStatus,
    mqttStatus,
    signalingError,
    mqttError,
    selectionError,
    shouldShowEmptyState,
    selectedVehicleId,
    loginId,
    onLogout,
    onReload,
    onSelectVehicle,
    // onConnect 는 더 이상 사용하지 않음(별도 connect 버튼 제거 — 카드 클릭으로 연결).
    onSelectAndConnect,
  } = props;

  const controlRequests = useFleetControlRequests();

  // Load vehicle meta (nicknames, photos, colors) from the integrations seam.
  // Use a plain Record instead of Map<> to avoid name collision with the
  // react-map-gl `Map` component import.
  const [vehicleMetaMap, setVehicleMetaMap] = useState<Record<string, VehicleMeta>>({});

  // 유저 칩 드롭다운(클릭 시 로그아웃 노출). 바깥 클릭/Esc 로 닫힘.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    accountSource.listVehicleMeta().then((list) => {
      if (cancelled) return;
      const record: Record<string, VehicleMeta> = {};
      list.forEach((m) => { record[m.vehicleId] = m; });
      setVehicleMetaMap(record);
    }).catch(() => {
      // meta load failure is non-critical; fall back to vehicleId
    });
    return () => { cancelled = true; };
  }, []);

  const selectedVehicle = vehicleList.find((v) => v.vehicleId === selectedVehicleId) || null;

  // ── 지도 분포(클러스터링) ──
  // 예전엔 고정 좌표/고정 zoom 에 머물러 있어, 차량이 어디 있는지 직접 찾아다녀야 했다.
  // 이제 위치를 보고한 차량 전체가 들어오도록 맞추고, 가까운 차량은 숫자 배지로 접는다.
  // 배지를 누르면 그 차량들만 담기게 확대되고, 확대될수록 묶음이 저절로 풀린다.
  const mapRef = useRef<MapRef | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapZoom, setMapZoom] = useState(FLEET_MAP_INITIAL_ZOOM);

  const locatedVehicles = useMemo(
    () => vehicleList.filter((vehicle) => vehicle.position != null),
    [vehicleList],
  );
  const locatedCount = locatedVehicles.length;

  const clusters = useMemo(
    () =>
      clusterByPixelDistance(
        locatedVehicles,
        (vehicle) => vehicle.vehicleId,
        (vehicle) => vehicle.position ?? null,
        mapZoom,
        CLUSTER_RADIUS_PX,
      ),
    [locatedVehicles, mapZoom],
  );

  const fitToVehicles = useCallback((vehicles: MergedVehicleInfo[]) => {
    const map = mapRef.current;
    const bounds = getBounds(
      vehicles
        .map((vehicle) => vehicle.position)
        .filter((position): position is NonNullable<typeof position> => position != null),
    );
    if (!map || !bounds) {
      return;
    }
    map.fitBounds(bounds, {
      padding: FIT_BOUNDS_PADDING_PX,
      maxZoom: FIT_BOUNDS_MAX_ZOOM,
      duration: 600,
    });
  }, []);

  // 위치를 보고하는 차량 "집합"이 바뀔 때만 전체 분포로 맞춘다. 좌표가 미세하게
  // 갱신될 때마다 카메라가 튀면 오히려 보기 어려우므로 id 집합을 키로 쓴다.
  const locatedVehicleKey = locatedVehicles
    .map((vehicle) => vehicle.vehicleId)
    .sort()
    .join("|");
  useEffect(() => {
    if (!mapReady || locatedVehicleKey === "") {
      return;
    }
    fitToVehicles(locatedVehicles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, locatedVehicleKey, fitToVehicles]);

  const handleClusterClick = useCallback(
    (cluster: FleetCluster<MergedVehicleInfo>) => {
      fitToVehicles(cluster.items);
    },
    [fitToVehicles],
  );

  // ── 휠/게임패드 내비게이션 포커스 상태 ──
  // selectedVehicleId(연결 대상)와 별개로, 휠 회전으로 이동하는 "강조(focus)" 인덱스.
  // 마우스/키보드 사용 시에도 selectedVehicleId 와 동기화해 일관된 강조를 유지.
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [wheelConnected, setWheelConnected] = useState(false);
  // 최신 목록/포커스를 콜백에서 참조하기 위한 ref(스테일 클로저 방지).
  const focusedIndexRef = useRef(0);
  const vehicleListRef = useRef<MergedVehicleInfo[]>(vehicleList);
  focusedIndexRef.current = focusedIndex;
  vehicleListRef.current = vehicleList;

  // "연결 중" 모달: 카드 클릭 순간부터 연결 완료(부모가 화면 전환)/실패까지 오버레이를 띄워
  // 사용자가 한 번 더 클릭하는 것을 막는다.
  const [connectingId, setConnectingId] = useState<string | null>(null);
  // 주행 전 준비 — 페달 프로파일(가속/브레이크 곡선) 설정 진입점.
  const [pedalProfileOpen, setPedalProfileOpen] = useState(false);

  const requestConnect = useCallback(
    (vehicleId: string) => {
      const vehicle = vehicleListRef.current.find(
        (v) => v.vehicleId === vehicleId,
      );
      if (!vehicle) return;
      // signaling + MQTT 가 "모두" 레디되기 전에는 연결을 시도하지 않는다(요청).
      // 예) "MQTT ready only"(signaling 미가용) 상태의 카드는 클릭해도 아무 동작 없음 —
      // 예전엔 준비 안 된 클릭을 latch 해두었다가 접속을 시도해 "연결 중" 이 떴다.
      if (!canSelectVehicle(vehicle)) {
        return;
      }
      setConnectingId(vehicleId); // "연결 중" 모달로 중복 클릭 방지
      onSelectAndConnect(vehicleId);
    },
    [onSelectAndConnect],
  );

  // Close the modal if the connection fails (error) so the user can retry.
  useEffect(() => {
    if (selectionError || signalingError || mqttError) setConnectingId(null);
  }, [selectionError, signalingError, mqttError]);

  // selectedVehicleId 가 외부에서 바뀌면 포커스를 그 항목으로 맞춤.
  useEffect(() => {
    if (!selectedVehicleId) return;
    const idx = vehicleList.findIndex((v) => v.vehicleId === selectedVehicleId);
    if (idx >= 0) {
      setFocusedIndex(idx);
    }
  }, [selectedVehicleId, vehicleList]);

  // 목록이 줄어들어 포커스가 범위를 벗어나면 보정.
  useEffect(() => {
    if (focusedIndex > vehicleList.length - 1) {
      setFocusedIndex(Math.max(0, vehicleList.length - 1));
    }
  }, [focusedIndex, vehicleList.length]);

  const moveFocus = useCallback((delta: number) => {
    const list = vehicleListRef.current;
    if (list.length === 0) return;
    const next = (focusedIndexRef.current + delta + list.length) % list.length;
    setFocusedIndex(next);
    onSelectVehicle(list[next].vehicleId);
  }, [onSelectVehicle]);

  // 게이밍 휠로 차량을 옮겨 고르면 그 차량 주변으로 지도를 이동한다(요청).
  // 최초 렌더에서는 전체 분포 맞춤(fitToVehicles)을 덮어쓰지 않도록 한 번 건너뛴다.
  const focusedVehicle = vehicleList[focusedIndex] ?? null;
  const focusedVehicleId = focusedVehicle?.vehicleId ?? "";
  const didInitialFocusPanRef = useRef(false);
  useEffect(() => {
    if (!mapReady) {
      return;
    }
    if (!didInitialFocusPanRef.current) {
      didInitialFocusPanRef.current = true;
      return;
    }
    const map = mapRef.current;
    const position = focusedVehicle?.position;
    if (!map || !position) {
      return;
    }
    const currentZoom = typeof map.getZoom === "function" ? map.getZoom() : 0;
    map.easeTo({
      center: [position.longitude, position.latitude],
      zoom: Math.max(currentZoom, FOCUS_MIN_ZOOM),
      duration: 500,
    });
    // 포커스가 "바뀔 때"만 반응 — 좌표가 갱신될 때마다 카메라가 흔들리지 않게 id 를 키로 쓴다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, focusedVehicleId]);

  const handleWheelEvent = useCallback(
    (event: WheelMenuEvent) => {
      switch (event) {
        case "UP":
        case "LEFT":
          moveFocus(-1);
          break;
        case "DOWN":
        case "RIGHT":
          moveFocus(1);
          break;
        case "CONFIRM": {
          const list = vehicleListRef.current;
          const vehicle = list[focusedIndexRef.current];
          if (vehicle) {
            requestConnect(vehicle.vehicleId);
          }
          break;
        }
        case "BACK":
          // 차량선택 화면에서 BACK = 로그아웃.
          onLogout();
          break;
      }
    },
    [moveFocus, requestConnect, onLogout],
  );

  useWheelMenuNav({
    onEvent: handleWheelEvent,
    onConnectionChange: setWheelConnected,
  });

  const handleCardClick = (vehicle: MergedVehicleInfo) => {
    // requestConnect 가 canSelectVehicle(signaling+MQTT 모두 레디)일 때만 연결한다.
    // 준비 안 된 카드 클릭은 여기서 vehicleId 를 넘겨도 no-op 이 된다.
    requestConnect(vehicle.vehicleId);
  };

  return (
    <div className="fs-fleet">
      {/* 차량이 제어를 요청하면 여기 목록으로 뜬다.
          여러 대가 동시에 요청할 수 있으므로 "수락" 버튼 하나가 아니라 차량 이름
          자체를 눌러 고르게 한다 — 어느 차량을 받는지가 곧 선택이다. */}
      {controlRequests.length > 0 ? (
        <div
          className="fs-reqs"
          role="dialog"
          aria-label="차량 제어 요청"
          data-testid="fleet-control-requests"
        >
          <div className="fs-reqs__head">
            <b>제어 요청</b>
            <span className="fs-reqs__count">{controlRequests.length}대</span>
          </div>
          <p className="fs-reqs__desc">
            차량이 원격 제어를 요청했습니다. 받을 차량을 선택하세요.
          </p>
          <ul className="fs-reqs__list">
            {controlRequests.map((request) => (
              <li key={request.vehicleId}>
                <button
                  type="button"
                  className="fs-reqs__item"
                  data-testid={`fleet-control-request-${request.vehicleId}`}
                  onClick={() => {
                    clearFleetControlRequest(request.vehicleId);
                    onSelectAndConnect(request.vehicleId);
                  }}
                >
                  <span className="fs-reqs__name">
                    {vehicleMetaMap[request.vehicleId]?.nickname ||
                      request.vehicleId}
                  </span>
                  <span className="fs-reqs__id">{request.vehicleId}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {/* ── Trust / header bar ── */}
      <div className="fs-trust">
        <span className="fs-tchip">차량 모니터링</span>

        {/* visually-hidden status elements preserved for tests */}
        <span className="fs-status-hidden" data-testid="signaling-auth-status">
          Signaling: {formatConnectionState(signalingStatus)}
        </span>
        <span className="fs-status-hidden" data-testid="mqtt-auth-status">
          MQTT: {formatConnectionState(mqttStatus)}
        </span>

        <div className="fs-trust-spacer" />

        {/* 유저 칩 — 우측 상단. 클릭하면 로그아웃이 드롭다운으로 펼쳐진다(우측 정렬). */}
        <div className="fs-user-menu fs-user-menu--right" ref={userMenuRef}>
          <button
            type="button"
            className="fs-tchip ok fs-user-chip"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            data-testid="fs-user-chip"
            onClick={() => setUserMenuOpen((o) => !o)}
          >
            <span className="fs-dot" />
            <span>{loginId}</span>
            <svg
              className={`fs-user-caret${userMenuOpen ? " open" : ""}`}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {userMenuOpen && (
            <div className="fs-user-dropdown" role="menu">
              <button
                type="button"
                className="fs-user-dropdown-item logout"
                role="menuitem"
                data-testid="fs-iconbtn-logout"
                onClick={() => {
                  setUserMenuOpen(false);
                  onLogout();
                }}
              >
                로그아웃
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── 2-column body ── */}
      <div className="fs-body">
        {/* LEFT: real Mapbox map with deterministic placeholder vehicle markers */}
        <div className="fs-map">
          <Map
            ref={mapRef}
            initialViewState={{
              longitude: FLEET_MAP_CENTER.longitude,
              latitude: FLEET_MAP_CENTER.latitude,
              zoom: FLEET_MAP_INITIAL_ZOOM,
            }}
            style={{ width: "100%", height: "100%" }}
            mapStyle={MAPBOX_STYLE}
            mapboxAccessToken={MAPBOX_TOKEN}
            onLoad={() => setMapReady(true)}
            onMove={(event) => setMapZoom(event.viewState.zoom)}
          >
            {/* 실시간 위치를 보고한 차량만 지도에 표시. 위치 없는 차량은 마커 없음(가짜 좌표 금지).
                가까운 차량은 숫자 배지 하나로 접고, 배지를 누르면 그 차량들만 담기게 확대한다. */}
            {clusters
              .filter((cluster) => cluster.items.length > 1)
              .map((cluster) => (
                <Marker
                  key={`cluster-${cluster.id}`}
                  longitude={cluster.longitude}
                  latitude={cluster.latitude}
                  anchor="center"
                >
                  <button
                    type="button"
                    className={[
                      "fs-cluster",
                      // 선택/포커스된 차량이 이 묶음 안에 있으면 강조 — 휠로 고른 차량이
                      // 접혀 있어도 어디 있는지 보이게 한다.
                      cluster.items.some(
                        (item) => item.vehicleId === selectedVehicleId,
                      )
                        ? "has-selected"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    data-testid={`fleet-cluster-${cluster.id}`}
                    title={`이 지역 차량 ${cluster.items.length}대 — 눌러서 확대`}
                    aria-label={`이 지역 차량 ${cluster.items.length}대. 눌러서 확대`}
                    onClick={() => handleClusterClick(cluster)}
                  >
                    <span className="fs-cluster-count">{cluster.items.length}</span>
                  </button>
                </Marker>
              ))}
            {clusters
              .filter((cluster) => cluster.items.length === 1)
              .map((cluster) => {
                const vehicle = cluster.items[0];
                const position = vehicle.position!;
                const meta = vehicleMetaMap[vehicle.vehicleId];
                const nickname = meta?.nickname || vehicle.vehicleId;
                const isSelected = vehicle.vehicleId === selectedVehicleId;
                const canConnect = canSelectVehicle(vehicle);
                const dotClass = getStatusDotClass(vehicle.signalingStatus);

                return (
                  <Marker
                    key={vehicle.vehicleId}
                    longitude={position.longitude}
                    latitude={position.latitude}
                    anchor="bottom"
                  >
                    <div
                      className={[
                        "fs-navpin",
                        `c-${dotClass}`,
                        isSelected ? "selected" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      title={
                        canConnect ? nickname : `${nickname} — ${vehicle.reason}`
                      }
                      onClick={() => requestConnect(vehicle.vehicleId)}
                      style={{ cursor: canConnect ? "pointer" : "default" }}
                    >
                      <span className="fs-navpin-label">
                        {nickname.split(" — ")[0]}
                      </span>
                      <svg
                        className="fs-navpin-marker"
                        viewBox="0 0 24 36"
                        width="30"
                        height="44"
                        aria-hidden="true"
                      >
                        <path
                          className="fs-navpin-body"
                          d="M12 0C5.373 0 0 5.373 0 12c0 8.5 12 24 12 24s12-15.5 12-24C24 5.373 18.627 0 12 0z"
                        />
                        <circle cx="12" cy="12" r="4.5" fill="#fff" />
                      </svg>
                    </div>
                  </Marker>
                );
              })}
          </Map>

          {/* 실시간 위치 안내 — 위치 보고 차량 수 / 위치 없는 차량 안내 */}
          <div className="fs-map-notice">
            {locatedCount > 0
              ? `실시간 위치 표시 중 · ${locatedCount}/${vehicleList.length}대 위치 수신`
              : "위치를 보고하는 차량이 없습니다 (위치 없음)"}
            {locatedCount > 1 && (
              <button
                type="button"
                className="fs-map-fitall"
                data-testid="fleet-map-fit-all"
                onClick={() => fitToVehicles(locatedVehicles)}
              >
                전체 보기
              </button>
            )}
          </div>
        </div>

        {/* RIGHT: vehicle list */}
        <div className="fs-list">
          <div className="fs-list-header">
            <span>접근가능 차량 {vehicleList.length} · 수신전용</span>
            {/* 새로고침 — 리스트 헤더 우측 아이콘 버튼. */}
            <button
              type="button"
              className="fs-pedal-profile-btn"
              data-testid="fleet-pedal-profile"
              title="페달 프로파일(가속/브레이크 곡선) 설정"
              onClick={() => setPedalProfileOpen(true)}
            >
              페달 설정
            </button>
            <button
              type="button"
              className="fs-list-refresh"
              title="새로고침"
              aria-label="차량 목록 새로고침"
              data-testid="fs-list-refresh"
              onClick={onReload}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>

          <div className="fs-list-scroll">
            {/* 로딩 중 — 두 채널 중 하나라도 로딩 중이면 스피너 표시 */}
            {(signalingStatus === "loading" || mqttStatus === "loading") && vehicleList.length === 0 && (
              <div className="fs-loading" role="status" aria-live="polite" aria-label="차량 목록 불러오는 중">
                <span className="fs-loading-spinner" aria-hidden="true" />
                <span>차량 목록 불러오는 중…</span>
              </div>
            )}
            {/* iter 10: role="status" — AT announces when vehicle list becomes empty */}
            {shouldShowEmptyState && (
              <div className="fs-empty" role="status" aria-live="polite">표시 가능한 차량이 없습니다.</div>
            )}

            {vehicleList.map((vehicle, index) => {
              const meta = vehicleMetaMap[vehicle.vehicleId];
              const nickname = meta?.nickname || vehicle.vehicleId;
              const color = meta?.color;
              const photoUrl = meta?.photoUrl;
              const isSelected = vehicle.vehicleId === selectedVehicleId;
              // 휠/게임패드로 강조된 항목(연결 대상과 별개의 시각 강조).
              const isFocused = index === focusedIndex;
              // MQTT 미연결 → 희미하게(dim) + 연결 불가. signaling 가용 시에만 클릭/연결.
              const mqttUp = isMqttConnected(vehicle.mqttState);
              const canConnect = canSelectVehicle(vehicle);
              const dotClass = getStatusDotClass(vehicle.signalingStatus);

              return (
                <div
                  key={vehicle.vehicleId}
                  className={[
                    "fs-vrow",
                    isSelected ? "selected" : "",
                    isFocused ? "wheel-focused" : "",
                    mqttUp ? "" : "dim",
                    canConnect ? "" : "disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  data-testid={`fleet-vehicle-${vehicle.vehicleId}`}
                  onClick={() => handleCardClick(vehicle)}
                  role="button"
                  tabIndex={canConnect ? 0 : -1}
                  aria-disabled={!canConnect}
                  onKeyDown={(e) => {
                    if (canConnect && (e.key === "Enter" || e.key === " ")) {
                      handleCardClick(vehicle);
                    }
                  }}
                >
                  {/* Thumbnail */}
                  <div
                    className="fs-thumb"
                    style={color ? { background: color + "22", color } : undefined}
                  >
                    {photoUrl ? (
                      <img src={photoUrl} alt={nickname} />
                    ) : (
                      getInitials(nickname)
                    )}
                  </div>

                  {/* Info — MQTT/signaling 텍스트 배지 제거. 연결 상태는 우측 점 인디케이터로만. */}
                  <div className="fs-vinfo">
                    <div className="fs-vname">{nickname}</div>
                    <div className="fs-vid">{vehicle.vehicleId}</div>
                    {/* 실시간 위치 미수신 차량은 가짜 좌표 대신 "위치 없음" 명시. */}
                    {vehicle.position == null && (
                      <div className="fs-no-position" data-testid={`fleet-no-position-${vehicle.vehicleId}`}>
                        위치 없음
                      </div>
                    )}
                    {!canConnect && (
                      <div className="fs-reason">
                        {vehicle.reason || "연결 불가"}
                      </div>
                    )}
                  </div>
                  {/* 상태 인디케이터 — 색 + 기호/텍스트 병용으로 색맹 접근성 확보.
                      ok=체크(✓), warn=느낌표(!), err=X, muted=줄표(–) */}
                  <span
                    className={`fs-status-dot ${dotClass}`}
                    title={
                      canConnect
                        ? "연결 가능"
                        : vehicle.reason || "연결 불가"
                    }
                    aria-label={canConnect ? "연결 가능" : (vehicle.reason || "연결 불가")}
                  >
                    {dotClass === "ok" ? "✓" : dotClass === "warn" ? "!" : dotClass === "err" ? "✕" : "–"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 빈 공간 흡수 — 차량이 적을 때 목록 아래의 빈 흰 공간을 제거 */}
          <div className="fs-list-spacer" aria-hidden="true" />

          {/* 에러 배너 — AWK 토큰 사용. 인라인 color hex 제거. */}
          {signalingError && (
            <div className="fs-error-banner" role="alert">
              시그널링 오류: {signalingError}
            </div>
          )}
          {mqttError && (
            <div className="fs-error-banner" role="alert">
              MQTT 오류: {mqttError}
            </div>
          )}
          {selectionError && (
            <div className="fs-error-banner" role="alert">
              {selectionError}
            </div>
          )}

          {/* Connect button area + sr-only select (keeps tests that use #connection-list / role=combobox working) */}
          <div className="fs-sr-connect-area">
            {/* Visually-hidden select — same options as the original dropdown.
                Programmatic selection via #connection-list still works;
                tests using getByRole("combobox") and getByRole("option") still pass. */}
            <select
              id="connection-list"
              className="fs-sr-only"
              onChange={(e) => onSelectVehicle(e.target.value)}
              value={selectedVehicleId}
              aria-label="차량 선택"
            >
              <option value="">-- Select vehicle --</option>
              {vehicleList.map((vehicle) => (
                <option value={vehicle.vehicleId} key={vehicle.vehicleId}>
                  {buildVehicleLabel(vehicle.vehicleId, vehicle)}
                </option>
              ))}
            </select>

            {/* iter 08: selected vehicle status — always show context so the disabled reason is clear */}
            {selectedVehicle ? (
              <p
                id="fs-connect-reason"
                data-testid="selected-vehicle-reason"
                className={
                  selectedVehicle.selectable
                    ? "fs-selected-reason"
                    : "fs-selected-reason fs-selected-reason--err"
                }
              >
                {selectedVehicle.reason}
              </p>
            ) : (
              /* iter 09: no selection — guide text so user knows to pick a vehicle card first */
              <p id="fs-connect-reason" className="fs-selected-reason fs-connect-hint">
                카드를 클릭하면 바로 연결됩니다
              </p>
            )}

            {/* 별도 connect 버튼 제거 — 카드 클릭만으로 즉시 연결되므로 중복(의미 없음). */}
          </div>
        </div>
      </div>

      {/* 휠/게임패드 조작 안내 — 화면 하단 고정 힌트 + 펼침형 도움말 */}
      <WheelHelpOverlay
        connected={wheelConnected}
        contextHint="휠 회전으로 차량 이동 · 가속페달/X/A로 연결 · 브레이크/○/B로 로그아웃"
      />

      <PedalProfileModal
        open={pedalProfileOpen}
        onClose={() => setPedalProfileOpen(false)}
      />

      {/* "연결 중" 모달 — 카드 클릭 후 연결이 완료될 때까지 표시해 중복 클릭을 막는다. */}
      {connectingId && (
        <div
          className="fs-connecting-overlay"
          role="alertdialog"
          aria-busy="true"
          aria-live="assertive"
          data-testid="fs-connecting-modal"
        >
          <div className="fs-connecting-modal">
            <div className="fs-connecting-spinner" aria-hidden="true" />
            <p className="fs-connecting-title">연결 중…</p>
            <p className="fs-connecting-sub">
              <strong>{vehicleMetaMap[connectingId]?.nickname || connectingId}</strong>{" "}
              연결 중입니다. 잠시만 기다려주세요.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

FleetSelectComponent.displayName = "FleetSelect";

export const FleetSelect = React.memo(FleetSelectComponent);
